import React, { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Layers, Upload, History, FileText, Plus, RotateCcw, Trash2,
  FileSpreadsheet, Edit3, CheckCircle2, AlertTriangle, Package,
} from 'lucide-react';
import ProductCatalogAdmin from '@/components/ProductCatalogAdmin';

const NETWORKS = ['QUBE', 'UFO', 'PVR INOX'];

function ProposalSetup({ entity, theatreCat, onSave }) {
  // Local draft state so edits don't fight the parent reload.
  const [approvalMode, setApprovalMode] = useState(entity?.approval_mode || 'always');
  const [thresholdPct, setThresholdPct] = useState(entity?.approval_threshold_pct ?? 10);
  const [durationOptions, setDurationOptions] = useState((theatreCat?.duration_options || [10, 20, 30, 40, 50, 60]).join(','));
  const [mults, setMults] = useState({ ...(theatreCat?.duration_multipliers || {}) });
  // Email setup
  const [senderEmail, setSenderEmail] = useState(entity?.sender_email || 'proposals@crm.shineadagency.com');
  const [senderName,  setSenderName]  = useState(entity?.sender_name  || 'Shine Ad Agency');
  const [domains, setDomains] = useState([]);
  const [domainsError, setDomainsError] = useState('');

  useEffect(() => {
    setApprovalMode(entity?.approval_mode || 'always');
    setThresholdPct(entity?.approval_threshold_pct ?? 10);
    setSenderEmail(entity?.sender_email || 'proposals@crm.shineadagency.com');
    setSenderName(entity?.sender_name   || 'Shine Ad Agency');
  }, [entity]);
  useEffect(() => {
    setDurationOptions((theatreCat?.duration_options || [10, 20, 30, 40, 50, 60]).join(','));
    setMults({ ...(theatreCat?.duration_multipliers || {}) });
  }, [theatreCat]);
  useEffect(() => {
    // Verified-domain picker — purely informational; the final check runs on
    // the actual Send too.
    api.get('/proposals/resend-domains')
      .then((r) => { setDomains(r.data.domains || []); setDomainsError(r.data.error || ''); })
      .catch(() => setDomainsError('Could not reach Resend to list domains'));
  }, []);

  if (!entity && !theatreCat) return null;
  const parsedDurations = durationOptions.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean);
  const senderDomain = senderEmail.includes('@') ? senderEmail.split('@').pop().toLowerCase() : '';
  const verifiedDomains = domains.filter((d) => d.verified).map((d) => (d.name || '').toLowerCase());
  const domainOk = !!senderDomain && verifiedDomains.includes(senderDomain);
  const domainKnown = !!senderDomain && domains.some((d) => (d.name || '').toLowerCase() === senderDomain);
  const senderPreview = senderName ? `${senderName} <${senderEmail}>` : senderEmail;

  return (
    <Section
      title="Proposal setup"
      subtitle="Approval policy, email identity, and theatre-ad duration configuration."
      right={
        <Button
          size="sm"
          onClick={() => onSave({
            entity: {
              approval_mode: approvalMode,
              approval_threshold_pct: Number(thresholdPct) || 0,
              sender_email: senderEmail.trim(),
              sender_name:  senderName.trim(),
            },
            catalog: {
              duration_options: parsedDurations,
              duration_multipliers: Object.fromEntries(
                parsedDurations.map((d) => [String(d), Number(mults[String(d)]) || (d / (theatreCat?.base_duration_seconds || 10))])
              ),
            },
          })}
          data-testid="proposal-setup-save"
        >
          Save setup
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* ── Email ─────────────────────────────────────────── */}
        <div className="space-y-2 md:col-span-2 p-3 rounded-lg bg-page/40 border border-ink/10">
          <h4 className="text-sm font-semibold text-ink flex items-center gap-2">
            <span>Email</span>
            <span className="text-[11px] font-normal text-ink-2">(Owner/Admin only)</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sender name (display)</Label>
              <Input value={senderName} onChange={(e) => setSenderName(e.target.value)}
                     placeholder="Shine Ad Agency" data-testid="sender-name" />
            </div>
            <div>
              <Label className="text-xs">Sender email</Label>
              <Input value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)}
                     placeholder="proposals@crm.shineadagency.com"
                     data-testid="sender-email"
                     className={senderDomain && !domainOk ? 'border-amber-400 focus-visible:ring-amber-400' : ''} />
            </div>
          </div>
          <div className="text-[11px] text-ink-2 flex items-center gap-2">
            Preview:&nbsp;
            <code className="bg-white border border-ink/10 rounded px-1.5 py-0.5">{senderPreview}</code>
          </div>
          {domainsError ? (
            <p className="text-[11px] text-amber-700" data-testid="resend-domain-error">
              Could not list Resend domains ({domainsError.slice(0, 80)}). Make sure RESEND_API_KEY is valid.
            </p>
          ) : senderDomain && domainOk ? (
            <p className="text-[11px] text-emerald-700" data-testid="resend-domain-ok">
              Domain <b>{senderDomain}</b> is verified on Resend. Ready to send.
            </p>
          ) : senderDomain && domainKnown ? (
            <p className="text-[11px] text-amber-700" data-testid="resend-domain-pending">
              Domain <b>{senderDomain}</b> is in Resend but not yet verified. Finish DNS verification before sending.
            </p>
          ) : senderDomain ? (
            <p className="text-[11px] text-rose-700" data-testid="resend-domain-missing">
              Domain <b>{senderDomain}</b> is not configured on Resend. Add + verify it in the Resend dashboard first.
              Verified domains: {verifiedDomains.join(', ') || '(none)'}.
            </p>
          ) : null}
        </div>

        {/* ── Approval ─────────────────────────────────────── */}
        <div className="space-y-2">
          <Label className="text-xs">Approval required</Label>
          <Select value={approvalMode} onValueChange={setApprovalMode}>
            <SelectTrigger data-testid="approval-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="threshold">Only when manual discount is above the threshold</SelectItem>
              <SelectItem value="always">Always</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-xs mt-2 block">Discount threshold (%)</Label>
          <Input
            type="number"
            value={thresholdPct}
            onChange={(e) => setThresholdPct(+e.target.value)}
            disabled={approvalMode === 'always'}
            min={0}
            max={100}
            data-testid="approval-threshold"
          />
          <p className="text-[11px] text-ink-2">
            {approvalMode === 'always'
              ? 'Every proposal must be approved before Generate and Send.'
              : `Approval is required automatically when the manual discount is above ${thresholdPct}%. Sales can also send below-threshold proposals for approval voluntarily.`}
          </p>
        </div>

        {/* ── Duration ─────────────────────────────────────── */}
        <div className="space-y-2">
          <Label className="text-xs">Theatre-ad duration options (seconds, comma-separated)</Label>
          <Input
            value={durationOptions}
            onChange={(e) => setDurationOptions(e.target.value)}
            placeholder="10,20,30,40,50,60"
            data-testid="duration-options"
          />
          <Label className="text-xs mt-2 block">Duration multipliers</Label>
          <div className="grid grid-cols-3 gap-1.5">
            {parsedDurations.map((d) => (
              <div key={d} className="flex items-center gap-1 text-xs">
                <span className="w-10 text-right text-ink-2">{d}s</span>
                <Input
                  type="number"
                  step="0.1"
                  value={mults[String(d)] ?? (d / (theatreCat?.base_duration_seconds || 10))}
                  onChange={(e) => setMults({ ...mults, [String(d)]: e.target.value })}
                  className="h-7 text-xs"
                  data-testid={`mult-${d}`}
                />
                <span className="text-ink-2">×</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-ink-2">
            Card rate is quoted per {theatreCat?.base_duration_seconds || 10} seconds. Linear default: a 20-sec spot = 2× rate, 30-sec = 3×. Override per network as needed.
          </p>
        </div>
      </div>
    </Section>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <Card className="shine-card rounded-xl p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-base font-semibold text-ink">{title}</h3>
          {subtitle && <p className="text-xs text-ink-2 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </Card>
  );
}

function ImportDialog({ open, onClose, onImported }) {
  const [network, setNetwork] = useState('QUBE');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const doPreview = async () => {
    if (!file) return toast.error('Choose an Excel file');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/rate-catalogs/theatre/import-preview?network=${encodeURIComponent(network)}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read the file');
    } finally { setBusy(false); }
  };

  const doPublish = async () => {
    if (!preview?.cache_id) return;
    setBusy(true);
    try {
      const { data } = await api.post('/rate-catalogs/theatre/publish', {
        cache_id: preview.cache_id,
      });
      toast.success(`Published version ${data.version.version} · ${data.inserted} rows`);
      onImported?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally { setBusy(false); }
  };

  const close = () => {
    setFile(null); setPreview(null); setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-2xl" data-testid="rate-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Import rate sheet</DialogTitle>
        </DialogHeader>
        {!preview ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Network</Label>
              <Select value={network} onValueChange={setNetwork}>
                <SelectTrigger data-testid="rate-import-network"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NETWORKS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Excel file (xlsx)</Label>
              <Input ref={fileRef} type="file" accept=".xlsx"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     data-testid="rate-import-file" />
              <p className="text-[11px] text-ink-2 mt-1">
                QUBE workbooks can have multiple state sheets; PVR INOX has 2 header rows and
                Festive/Non-festive rate columns — both are detected automatically.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-ink" />
              <span><b>{preview.total_rows}</b> rows ready · {preview.problem_count} problem{preview.problem_count === 1 ? '' : 's'}</span>
            </div>
            <div className="rounded-lg border border-ink/10 p-3 bg-page">
              <div className="text-[11px] uppercase text-ink-2 mb-1">Sheets</div>
              <div className="flex flex-wrap gap-2">
                {preview.sheets.map((s, i) => (
                  <Badge key={i} variant="outline" className="rounded-md">{s.sheet} · {s.rows}</Badge>
                ))}
              </div>
            </div>
            {preview.problem_count > 0 && (
              <div className="rounded-lg border border-amber-300/50 bg-amber-50/50 p-3 text-xs">
                <div className="font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> First problems:</div>
                <ul className="list-disc pl-5 mt-1">
                  {preview.problems.slice(0, 10).map((p, i) => <li key={i}>Row {p.row}: {p.issue}</li>)}
                </ul>
              </div>
            )}
            <div className="rounded-lg border border-ink/10 p-3 bg-page text-xs overflow-x-auto">
              <div className="font-semibold text-ink mb-1">Sample row</div>
              <pre className="whitespace-pre-wrap">{JSON.stringify(preview.sample_rows?.[0] || {}, null, 2)}</pre>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={close} data-testid="rate-import-cancel">Cancel</Button>
          {!preview ? (
            <Button onClick={doPreview} disabled={busy || !file} data-testid="rate-import-preview-btn">
              {busy ? 'Reading…' : 'Preview'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)}>Back</Button>
              <Button onClick={doPublish} disabled={busy} data-testid="rate-import-publish-btn">
                {busy ? 'Publishing…' : `Publish ${preview.total_rows} rows`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RateCatalogPage() {
  const [tab, setTab] = useState('services');
  const [services, setServices] = useState([]);
  const [slabs, setSlabs] = useState({});
  const [versions, setVersions] = useState([]);
  const [terms, setTerms] = useState([]);
  // Messaging & conditions setup (new tabs)
  const [messagingRates, setMessagingRates] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [presets, setPresets] = useState([]);
  const [termsKind, setTermsKind] = useState('default');
  const [importOpen, setImportOpen] = useState(false);
  const [newTerms, setNewTerms] = useState({ open: false, body: '', title: '', kind: 'default' });
  const [editRate, setEditRate] = useState(null); // {id?, ...fields}
  const [editCompliance, setEditCompliance] = useState(null);
  const [editPreset, setEditPreset] = useState(null);
  // Proposal setup — approval policy + theatre duration config.
  const [entity, setEntity] = useState(null);
  const [theatreCat, setTheatreCat] = useState(null);
  // Workflow settings (approval toggle, market note, price-change threshold).
  const [workflow, setWorkflow] = useState({
    require_approval: false,
    price_change_threshold_pct: 10,
    market_change_note: { enabled: true, text: '' },
  });
  const [workflowDirty, setWorkflowDirty] = useState(false);

  const loadAll = async () => {
    try {
      const [svc, vers, trms, ents, tcat, mr, mc, cp, wf] = await Promise.all([
        api.get('/services'),
        api.get('/rate-catalogs/theatre/versions'),
        api.get('/terms'),
        api.get('/business-entities'),
        api.get('/rate-catalogs/theatre'),
        api.get('/messaging-rate-items').catch(() => ({ data: [] })),
        api.get('/messaging-compliance').catch(() => ({ data: [] })),
        api.get('/condition-presets').catch(() => ({ data: [] })),
        api.get('/settings/proposals-workflow').catch(() => ({ data: null })),
      ]);
      setServices(svc.data);
      setVersions(vers.data);
      setTerms(trms.data);
      setEntity((ents.data || []).find((e) => e.is_default) || (ents.data || [])[0] || null);
      setTheatreCat(tcat.data);
      if (wf.data) setWorkflow(wf.data);
      setMessagingRates(mr.data || []);
      setCompliance(mc.data || []);
      setPresets(cp.data || []);
      const slabMap = {};
      for (const s of svc.data) {
        if (s.pricing_mode === 'catalog') {
          try {
            const { data } = await api.get(`/discount-slabs?service_key=${s.key}`);
            slabMap[s.key] = data;
          } catch { slabMap[s.key] = []; }
        }
      }
      setSlabs(slabMap);
    } catch (e) {
      toast.error('Could not load catalog');
    }
  };
  useEffect(() => { loadAll(); }, []);

  const doRollback = async (vid) => {
    if (!window.confirm('Roll back to this version? The current Published version will be Superseded.')) return;
    try {
      await api.post(`/rate-catalogs/theatre/rollback/${vid}`);
      toast.success('Rolled back');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback failed');
    }
  };

  const saveNewTerms = async () => {
    if (!newTerms.body.trim()) return toast.error('Terms body required');
    try {
      await api.post('/terms', {
        title: newTerms.title || 'Terms & Conditions',
        body: newTerms.body,
        kind: newTerms.kind || 'default',
      });
      toast.success('New version saved');
      setNewTerms({ open: false, body: '', title: '', kind: newTerms.kind || 'default' });
      loadAll();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  // ── Messaging rate items CRUD ──
  const saveMsgRate = async () => {
    if (!editRate) return;
    const payload = {
      key: editRate.key,
      label: editRate.label,
      unit_label: editRate.unit_label || 'message',
      is_range: !!editRate.is_range,
      min_rate: Number(editRate.min_rate || 0),
      max_rate: Number(editRate.max_rate || 0),
      default_rate: Number(editRate.default_rate || 0),
      rate: Number(editRate.rate ?? editRate.default_rate ?? 0),
      example_qty: Number(editRate.example_qty || 10000),
      internal_note: editRate.internal_note || '',
      sequence: Number(editRate.sequence || 100),
      active: editRate.active !== false,
    };
    try {
      if (editRate.id) {
        await api.patch(`/messaging-rate-items/${editRate.id}`, payload);
      } else {
        await api.post('/messaging-rate-items', payload);
      }
      toast.success('Saved');
      setEditRate(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const removeMsgRate = async (id) => {
    if (!window.confirm('Delete this rate? Existing proposals keep their stored rates.')) return;
    try {
      await api.delete(`/messaging-rate-items/${id}`);
      loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  // ── Messaging compliance texts CRUD ──
  const saveCompliance = async () => {
    if (!editCompliance) return;
    const payload = {
      scope: editCompliance.scope,
      body: editCompliance.body || '',
      required: editCompliance.required !== false,
      sequence: Number(editCompliance.sequence || 100),
    };
    try {
      if (editCompliance.id) {
        await api.patch(`/messaging-compliance/${editCompliance.id}`, payload);
      } else {
        await api.post('/messaging-compliance', payload);
      }
      toast.success('Saved');
      setEditCompliance(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const removeCompliance = async (id) => {
    if (!window.confirm('Delete this compliance text?')) return;
    try {
      await api.delete(`/messaging-compliance/${id}`);
      loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  // ── Condition presets CRUD (Quick-add menu) ──
  const savePreset = async () => {
    if (!editPreset) return;
    const payload = {
      label: (editPreset.label || '').trim(),
      body: (editPreset.body || '').trim(),
      is_active: editPreset.is_active !== false,
      sequence: Number(editPreset.sequence || 100),
    };
    if (!payload.label || !payload.body) return toast.error('Label and body are required');
    try {
      if (editPreset.id) {
        await api.patch(`/condition-presets/${editPreset.id}`, payload);
      } else {
        await api.post('/condition-presets', payload);
      }
      toast.success('Saved');
      setEditPreset(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const removePreset = async (id) => {
    if (!window.confirm('Delete this preset clause? Existing proposals are unaffected.')) return;
    try {
      await api.delete(`/condition-presets/${id}`);
      loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="rate-catalog-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
            <Layers className="h-6 w-6 text-green-ink" /> Rate Catalog
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            Services, published rate sheets and quotation Terms & Conditions.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="shine-tabs">
          <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">Rate sheets</TabsTrigger>
          <TabsTrigger value="messaging-rates" data-testid="tab-messaging-rates">Messaging rates</TabsTrigger>
          <TabsTrigger value="product-catalog" data-testid="tab-product-catalog">
            <Package className="h-3.5 w-3.5 mr-1 inline" />Product Catalog
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-messaging-compliance">Messaging compliance</TabsTrigger>
          <TabsTrigger value="conditions" data-testid="tab-conditions-library">Conditions library</TabsTrigger>
          <TabsTrigger value="terms" data-testid="tab-terms">Terms</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="mt-4 space-y-4">
          <ProposalSetup
            entity={entity}
            theatreCat={theatreCat}
            onSave={async (patch) => {
              try {
                if (patch.entity && entity) {
                  await api.patch(`/business-entities/${entity.id}`, patch.entity);
                }
                if (patch.catalog && theatreCat) {
                  await api.patch(`/rate-catalogs/theatre`, patch.catalog);
                }
                toast.success('Proposal setup saved');
                loadAll();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Save failed');
              }
            }}
          />

          <Section title="Services" subtitle="Shown in the New Proposal step; order matches the sequence column.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-2 text-xs uppercase border-b border-ink/10">
                    <th className="py-2 pr-3">Seq</th>
                    <th className="py-2 pr-3">Service</th>
                    <th className="py-2 pr-3">Pricing mode</th>
                    <th className="py-2 pr-3">Catalog</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id} className="border-b border-ink/5">
                      <td className="py-2 pr-3">{s.sequence}</td>
                      <td className="py-2 pr-3 font-medium text-ink">{s.name}</td>
                      <td className="py-2 pr-3"><Badge variant="outline">{s.pricing_mode}</Badge></td>
                      <td className="py-2 pr-3 text-ink-2">{s.catalog_key || '—'}</td>
                      <td className="py-2 pr-3">
                        {s.active ? <Badge className="bg-green-tint text-green-ink border-0">Active</Badge>
                                 : <Badge variant="outline">Inactive</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Discount slabs" subtitle="Automatic budget-slab discount for Theatre Ads (no approval needed).">
            {Object.entries(slabs).map(([svc, rows]) => (
              <div key={svc} className="mb-2">
                <div className="text-xs text-ink-2 uppercase mb-1">{svc}</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-ink-2 text-xs uppercase border-b border-ink/10">
                      <th className="py-2 pr-3">Budget from</th>
                      <th className="py-2 pr-3">Budget to</th>
                      <th className="py-2 pr-3">Discount</th>
                      <th className="py-2 pr-3">Label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-ink/5">
                        <td className="py-2 pr-3">₹{r.budget_from.toLocaleString('en-IN')}</td>
                        <td className="py-2 pr-3">{r.budget_to ? `₹${r.budget_to.toLocaleString('en-IN')}` : '∞'}</td>
                        <td className="py-2 pr-3 font-medium text-green-ink">{r.discount_pct}%</td>
                        <td className="py-2 pr-3 text-ink-2">{r.label || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </Section>
        </TabsContent>

        <TabsContent value="rates" className="mt-4 space-y-4">
          <Section
            title="Theatre rate sheets"
            subtitle="QUBE / UFO / PVR INOX. Each publish creates a new version; prior Published becomes Superseded. Rollback restores an older one."
            right={
              <Button onClick={() => setImportOpen(true)} data-testid="rate-import-open-btn">
                <Upload className="h-4 w-4 mr-1" /> Import rate sheet
              </Button>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-2 text-xs uppercase border-b border-ink/10">
                    <th className="py-2 pr-3">Network</th>
                    <th className="py-2 pr-3">Version</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Rows</th>
                    <th className="py-2 pr-3">Published</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.id} className="border-b border-ink/5">
                      <td className="py-2 pr-3 font-medium text-ink">{v.network}</td>
                      <td className="py-2 pr-3">v{v.version}</td>
                      <td className="py-2 pr-3">
                        {v.status === 'Published' && <Badge className="bg-green-tint text-green-ink border-0">Published</Badge>}
                        {v.status === 'Superseded' && <Badge variant="outline" className="text-ink-2">Superseded</Badge>}
                        {v.status === 'Draft' && <Badge variant="outline">Draft</Badge>}
                      </td>
                      <td className="py-2 pr-3 tabular-nums">{v.row_count || 0}</td>
                      <td className="py-2 pr-3 text-ink-2 text-xs">
                        {v.published_at ? new Date(v.published_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 pr-3 text-ink-2 truncate max-w-[260px] text-xs">{v.source_filename || '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        {v.status === 'Superseded' && (
                          <Button size="sm" variant="outline" onClick={() => doRollback(v.id)}
                                  data-testid={`rate-rollback-${v.id}`}>
                            <RotateCcw className="h-3 w-3 mr-1" /> Rollback
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {versions.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-ink-2">No rate sheets yet. Import the first one.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="messaging-rates" className="mt-4 space-y-4" data-testid="messaging-rates-tab">
          <Section
            title="Messaging & Voice Campaigns — rate items"
            subtitle="Owner/Admin edits min/max/default rates for each channel. Changes do not affect proposals already created."
            right={
              <Button onClick={() => setEditRate({ key: '', label: '', unit_label: 'message', is_range: false, min_rate: 0, max_rate: 0, default_rate: 0, rate: 0, sequence: 100, active: true })} data-testid="msg-rate-new-btn">
                <Plus className="h-4 w-4 mr-1" /> New messaging rate
              </Button>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="messaging-rate-table">
                <thead className="text-left text-xs text-ink-2 uppercase border-b border-ink/10">
                  <tr>
                    <th className="py-2 pr-3">Channel</th>
                    <th className="py-2 pr-3">Unit</th>
                    <th className="py-2 pr-3">Range</th>
                    <th className="py-2 pr-3 text-right">Default (₹)</th>
                    <th className="py-2 pr-3">Active</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {messagingRates.map((r) => (
                    <tr key={r.id} className="border-b border-ink/5" data-testid={`msg-rate-row-${r.key}`}>
                      <td className="py-2 pr-3">
                        <div className="font-medium">{r.label}</div>
                        <div className="text-[11px] text-ink-2 font-mono">{r.key}</div>
                      </td>
                      <td className="py-2 pr-3">{r.unit_label}</td>
                      <td className="py-2 pr-3 text-xs">
                        {r.is_range ? `${Number(r.min_rate).toFixed(2)} – ${Number(r.max_rate).toFixed(2)}` : 'fixed'}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{Number(r.default_rate).toFixed(2)}</td>
                      <td className="py-2 pr-3">
                        {r.active ? <Badge className="bg-green-tint text-green-ink border-0">Active</Badge> : <Badge variant="outline">Inactive</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditRate(r)} data-testid={`msg-rate-edit-${r.key}`}>
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeMsgRate(r.id)} data-testid={`msg-rate-delete-${r.key}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {messagingRates.length === 0 && (
                    <tr><td colSpan={6} className="py-6 text-center text-ink-2 text-sm">No messaging rates yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="product-catalog" className="mt-4 space-y-4" data-testid="product-catalog-tab">
          <ProductCatalogAdmin />
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 space-y-4" data-testid="compliance-tab">
          <Section
            title="Messaging compliance confirmations"
            subtitle="Internal checklist shown in the wizard before Generate. Never printed on the client PDF."
            right={
              <Button onClick={() => setEditCompliance({ scope: 'all', body: '', required: true, sequence: 100 })} data-testid="compliance-new-btn">
                <Plus className="h-4 w-4 mr-1" /> New compliance text
              </Button>
            }
          >
            <div className="space-y-2">
              {compliance.map((c) => (
                <div key={c.id} className="rounded-lg border border-ink/10 p-3 flex items-start gap-3" data-testid={`compliance-row-${c.id}`}>
                  <Badge variant="outline" className="text-[10px] uppercase mt-0.5">{c.scope}</Badge>
                  {c.required && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] uppercase mt-0.5">Required</Badge>}
                  <div className="flex-1 text-sm text-ink">{c.body}</div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditCompliance(c)}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => removeCompliance(c.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </div>
                </div>
              ))}
              {compliance.length === 0 && <div className="text-sm text-ink-2">No compliance texts yet.</div>}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="conditions" className="mt-4 space-y-4" data-testid="conditions-library-tab">
          <Section
            title="Conditions library (Quick-add presets)"
            subtitle="Reusable clauses salespeople can drop into a proposal's conditions. Managed by Owner/Admin only; Sales cannot write."
            right={
              <Button onClick={() => setEditPreset({ label: '', body: '', is_active: true, sequence: 100 })} data-testid="preset-new-btn">
                <Plus className="h-4 w-4 mr-1" /> New preset clause
              </Button>
            }
          >
            <div className="space-y-2">
              {presets.map((p) => (
                <div key={p.id} className="rounded-lg border border-ink/10 p-3 flex items-start gap-3" data-testid={`preset-row-${p.id}`}>
                  <div className="flex-1">
                    <div className="font-semibold text-ink text-sm">{p.label}</div>
                    <div className="text-xs text-ink-2 whitespace-pre-wrap mt-1">{p.body}</div>
                  </div>
                  {!p.is_active && <Badge variant="outline">Retired</Badge>}
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditPreset(p)}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => removePreset(p.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </div>
                </div>
              ))}
              {presets.length === 0 && <div className="text-sm text-ink-2">No preset clauses yet. Add common conditions like "100% advance payment required", "Creative to be supplied by client", "GST extra".</div>}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="terms" className="mt-4 space-y-4">
          <Section
            title="Terms & Conditions versions"
            subtitle="The current version is attached to every new proposal; older proposals keep their original. Each kind (default / messaging) is tracked separately."
            right={
              <div className="flex items-center gap-2">
                <Select value={termsKind} onValueChange={setTermsKind}>
                  <SelectTrigger className="w-56 h-9" data-testid="terms-kind-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Advertising (default)</SelectItem>
                    <SelectItem value="messaging_campaign">Messaging &amp; Voice Campaign</SelectItem>
                    <SelectItem value="__all__">All kinds</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setNewTerms({ open: true, body: '', title: '', kind: termsKind === '__all__' ? 'default' : termsKind })} data-testid="terms-new-btn">
                  <Plus className="h-4 w-4 mr-1" /> New version
                </Button>
              </div>
            }
          >
            <div className="space-y-3">
              {terms.filter((t) => termsKind === '__all__' || (t.kind || 'default') === termsKind).map((t) => (
                <div key={t.id} className="rounded-lg border border-ink/10 p-3" data-testid={`terms-row-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-ink">v{t.version} · {t.title}</div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] uppercase">{t.kind || 'default'}</Badge>
                      {t.is_current && <Badge className="bg-green-tint text-green-ink border-0">Current</Badge>}
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap text-xs text-ink-2 mt-2 max-h-32 overflow-auto">{t.body}</pre>
                </div>
              ))}
              {terms.filter((t) => termsKind === '__all__' || (t.kind || 'default') === termsKind).length === 0 && (
                <div className="text-sm text-ink-2">No T&C versions saved yet for this kind.</div>
              )}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="workflow" className="mt-4 space-y-4" data-testid="workflow-tab">
          <Section
            title="Proposal workflow"
            subtitle="Approval gate, market-change note on PDFs, and price-change alerts. Owner / Admin only."
            right={
              <Button
                data-testid="workflow-save"
                disabled={!workflowDirty}
                onClick={async () => {
                  try {
                    const { data } = await api.patch('/settings/proposals-workflow', workflow);
                    setWorkflow(data);
                    setWorkflowDirty(false);
                    toast.success('Workflow settings saved');
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Save failed');
                  }
                }}
              >Save</Button>
            }
          >
            <div className="space-y-5">
              {/* Approval toggle */}
              <div className="flex items-start justify-between gap-6 rounded-lg border border-ink/10 p-4 bg-white">
                <div className="flex-1">
                  <div className="font-medium text-ink">Require approval for proposals</div>
                  <div className="text-xs text-ink-2 mt-1">
                    When OFF (default), proposals can be generated and sent without Owner approval.
                    When ON, everyone except the Owner must send proposals for approval first.
                  </div>
                </div>
                <Switch
                  checked={!!workflow.require_approval}
                  onCheckedChange={(v) => { setWorkflow({ ...workflow, require_approval: v }); setWorkflowDirty(true); }}
                  data-testid="workflow-require-approval"
                />
              </div>

              {/* Price-change alerts */}
              <div className="rounded-lg border border-ink/10 p-4 bg-white space-y-3">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="font-medium text-ink">Price-change alerts (Owner)</div>
                    <div className="text-xs text-ink-2 mt-1">
                      When a Sent / Generated proposal's total shifts by more than this percentage after an edit,
                      the Owner gets a bell notification. Set to <b>0</b> to disable alerts.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 max-w-xs">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={workflow.price_change_threshold_pct ?? 10}
                    onChange={(e) => { setWorkflow({ ...workflow, price_change_threshold_pct: Number(e.target.value) }); setWorkflowDirty(true); }}
                    data-testid="workflow-price-threshold"
                  />
                  <span className="text-sm text-ink-2">%</span>
                </div>
              </div>

              {/* Market-change note */}
              <div className="rounded-lg border border-ink/10 p-4 bg-white space-y-3">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="font-medium text-ink">Market-change note on PDFs</div>
                    <div className="text-xs text-ink-2 mt-1">
                      A short note printed on every quotation PDF (just below the terms).
                      Edit the text or turn it off. The current version number is <b>v{workflow.market_change_note?.version ?? 1}</b>.
                    </div>
                  </div>
                  <Switch
                    checked={!!workflow.market_change_note?.enabled}
                    onCheckedChange={(v) => { setWorkflow({ ...workflow, market_change_note: { ...workflow.market_change_note, enabled: v } }); setWorkflowDirty(true); }}
                    data-testid="workflow-market-note-enabled"
                  />
                </div>
                <textarea
                  className="w-full min-h-[90px] rounded-md border border-ink/15 p-2 text-sm bg-white"
                  value={workflow.market_change_note?.text || ''}
                  onChange={(e) => { setWorkflow({ ...workflow, market_change_note: { ...workflow.market_change_note, text: e.target.value } }); setWorkflowDirty(true); }}
                  placeholder="Market-change note text…"
                  data-testid="workflow-market-note-text"
                />
              </div>
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={loadAll} />

      <Dialog open={newTerms.open} onOpenChange={(v) => !v && setNewTerms({ ...newTerms, open: false })}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Terms & Conditions version</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kind</Label>
                <Select value={newTerms.kind || 'default'} onValueChange={(v) => setNewTerms({ ...newTerms, kind: v })}>
                  <SelectTrigger data-testid="new-terms-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Advertising (cinema/newspaper/custom)</SelectItem>
                    <SelectItem value="messaging_campaign">Messaging &amp; Voice Campaign</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={newTerms.title} onChange={(e) => setNewTerms({ ...newTerms, title: e.target.value })}
                       placeholder="Terms & Conditions" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Body (plain text)</Label>
              <textarea
                className="w-full min-h-[180px] rounded-md border border-ink/15 p-2 text-sm bg-white"
                value={newTerms.body}
                onChange={(e) => setNewTerms({ ...newTerms, body: e.target.value })}
                placeholder="Paste the latest T&C text here…"
                data-testid="terms-new-body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewTerms({ open: false, body: '', title: '', kind: 'default' })}>Cancel</Button>
            <Button onClick={saveNewTerms} data-testid="terms-new-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Messaging rate editor ── */}
      <Dialog open={!!editRate} onOpenChange={(v) => !v && setEditRate(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="msg-rate-dialog">
          <DialogHeader>
            <DialogTitle>{editRate?.id ? 'Edit messaging rate' : 'New messaging rate'}</DialogTitle>
          </DialogHeader>
          {editRate && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Label *</Label>
                <Input value={editRate.label || ''} onChange={(e) => setEditRate({ ...editRate, label: e.target.value })} placeholder="e.g. WhatsApp named sender" data-testid="msg-rate-label" />
              </div>
              <div>
                <Label className="text-xs">Key * (unique)</Label>
                <Input value={editRate.key || ''} onChange={(e) => setEditRate({ ...editRate, key: e.target.value })} disabled={!!editRate.id} placeholder="whatsapp_named" data-testid="msg-rate-key" />
              </div>
              <div>
                <Label className="text-xs">Billable unit</Label>
                <Select value={editRate.unit_label || 'message'} onValueChange={(v) => setEditRate({ ...editRate, unit_label: v })}>
                  <SelectTrigger data-testid="msg-rate-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="message">message</SelectItem>
                    <SelectItem value="segment">segment</SelectItem>
                    <SelectItem value="call unit">call unit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input id="is_range" type="checkbox" checked={!!editRate.is_range} onChange={(e) => setEditRate({ ...editRate, is_range: e.target.checked })} data-testid="msg-rate-is-range" />
                <Label htmlFor="is_range" className="text-xs">Allow range (salesperson picks a rate within min…max)</Label>
              </div>
              <div>
                <Label className="text-xs">Min rate (₹)</Label>
                <Input type="number" step="0.01" value={editRate.min_rate ?? 0} onChange={(e) => setEditRate({ ...editRate, min_rate: Number(e.target.value || 0) })} disabled={!editRate.is_range} data-testid="msg-rate-min" />
              </div>
              <div>
                <Label className="text-xs">Max rate (₹)</Label>
                <Input type="number" step="0.01" value={editRate.max_rate ?? 0} onChange={(e) => setEditRate({ ...editRate, max_rate: Number(e.target.value || 0) })} disabled={!editRate.is_range} data-testid="msg-rate-max" />
              </div>
              <div>
                <Label className="text-xs">Default rate (₹) *</Label>
                <Input type="number" step="0.01" value={editRate.default_rate ?? 0} onChange={(e) => setEditRate({ ...editRate, default_rate: Number(e.target.value || 0) })} data-testid="msg-rate-default" />
              </div>
              <div>
                <Label className="text-xs">Example qty (shown in Rate Catalog)</Label>
                <Input type="number" value={editRate.example_qty ?? 10000} onChange={(e) => setEditRate({ ...editRate, example_qty: Number(e.target.value || 0) })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Internal note (not printed on client PDF)</Label>
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-ink/15 p-2 text-sm bg-white"
                  value={editRate.internal_note || ''}
                  onChange={(e) => setEditRate({ ...editRate, internal_note: e.target.value })}
                  data-testid="msg-rate-internal-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRate(null)}>Cancel</Button>
            <Button onClick={saveMsgRate} data-testid="msg-rate-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Compliance text editor ── */}
      <Dialog open={!!editCompliance} onOpenChange={(v) => !v && setEditCompliance(null)}>
        <DialogContent data-testid="compliance-dialog">
          <DialogHeader>
            <DialogTitle>{editCompliance?.id ? 'Edit compliance text' : 'New compliance text'}</DialogTitle>
          </DialogHeader>
          {editCompliance && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={editCompliance.scope || 'all'} onValueChange={(v) => setEditCompliance({ ...editCompliance, scope: v })}>
                  <SelectTrigger data-testid="compliance-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All channels</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="rcs">RCS</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="ivr">IVR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editCompliance.required !== false} onChange={(e) => setEditCompliance({ ...editCompliance, required: e.target.checked })} id="comp-required" data-testid="compliance-required" />
                <Label htmlFor="comp-required" className="text-xs">Required to Generate</Label>
              </div>
              <div>
                <Label className="text-xs">Body</Label>
                <textarea
                  className="w-full min-h-[120px] rounded-md border border-ink/15 p-2 text-sm bg-white"
                  value={editCompliance.body || ''}
                  onChange={(e) => setEditCompliance({ ...editCompliance, body: e.target.value })}
                  data-testid="compliance-body"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditCompliance(null)}>Cancel</Button>
            <Button onClick={saveCompliance} data-testid="compliance-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Preset clause editor (conditions library) ── */}
      <Dialog open={!!editPreset} onOpenChange={(v) => !v && setEditPreset(null)}>
        <DialogContent data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle>{editPreset?.id ? 'Edit preset clause' : 'New preset clause'}</DialogTitle>
          </DialogHeader>
          {editPreset && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Label (shown in Quick-add menu)</Label>
                <Input value={editPreset.label || ''} onChange={(e) => setEditPreset({ ...editPreset, label: e.target.value })} placeholder="e.g. 100% advance payment" data-testid="preset-label" />
              </div>
              <div>
                <Label className="text-xs">Clause body</Label>
                <textarea
                  className="w-full min-h-[140px] rounded-md border border-ink/15 p-2 text-sm bg-white"
                  value={editPreset.body || ''}
                  onChange={(e) => setEditPreset({ ...editPreset, body: e.target.value })}
                  placeholder="e.g. 100% advance payment is required to lock the campaign."
                  data-testid="preset-body"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editPreset.is_active !== false} onChange={(e) => setEditPreset({ ...editPreset, is_active: e.target.checked })} id="preset-active" data-testid="preset-active" />
                <Label htmlFor="preset-active" className="text-xs">Active (visible in Quick-add)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditPreset(null)}>Cancel</Button>
            <Button onClick={savePreset} data-testid="preset-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
